import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {SessionContext} from "../context/SessionContextProvider";
import useMachines from "../hooks/useMachines";
import {parseLocation, parseNavigationSkill, parseStatus, parseWorkConfiguration} from "../utils/Parsers";
import OverviewMachineList from "./OverviewMachineList";
import useUserSessionInfo from "../hooks/useUserSessionInfo";
import UserHeader from "./UserHeader";
import OverviewParcelList from "./OverviewParcelList";
import useParcelsWithHulls from "../hooks/useParcelsWithHulls";
import packageJson from "../../package.json";
import useMachineFieldBidirectionalMapping from "../hooks/useMachineFieldBidirectionalMapping";
import {useNavigate} from "react-router-dom";
import * as Const from "../Const";
import {BASE_NAME} from "../Const";
import OverviewMapV4 from "./OverviewMapV4";
import {getSetFromCookie, getStringFromCookie, setCookieSet, setCookieString} from "../utils/Cookies"
import mqttClientSingleton from "../utils/MqttClientSingleton";
import useMqttSubscription from "../hooks/useMqttSubscription";


function MainPageV2() {

    const TAG = "MainV2";
    const version = packageJson.version;

    const { session } = useContext(SessionContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (!session) {
            navigate(Const.BASE_NAME + "/user/login");
        }
    });

    const onSessionError = useMemo(() => () => navigate(BASE_NAME + "/user/login"), [navigate]);

    const [sessionId, userId, userName, remoteAddr] = useUserSessionInfo(onSessionError);

    const mqttRef = useRef(null);

    useEffect(() => {
        const mqttOptions = {
            username: 'rm',
            password: '!UasMQTT',
        };

        const client = mqttClientSingleton.connect(Const.MQTT_BROKER_ADDRESS, mqttOptions);
        mqttRef.current = mqttClientSingleton;
        return () => {
            client.end();
        };
    }, []);



    const machines = useMachines(session, userId !== null);

    const parcelUpdateTimestamps = useMqttSubscription(mqttRef.current, machines, "synced/%")
    const [parcelsWithHulls, isParcelsLoading] = useParcelsWithHulls(session, machines, parcelUpdateTimestamps, userId !== null);
    const [hiddenParcels, setHiddenParcels] = useState(getSetFromCookie(Const.COOKIE_HIDDEN_PARCELS));

    const livePositions = useMqttSubscription(mqttRef.current, machines, "%/position", parseLocation);
    const machineVelocities = useMqttSubscription(mqttRef.current, machines, "%/velocity");
    const machineStates = useMqttSubscription(mqttRef.current, machines, "%/status", parseStatus);
    const navigationSkills = useMqttSubscription(mqttRef.current, machines, "%/active_navigation_skill", parseNavigationSkill);
    const workConfigurations = useMqttSubscription(mqttRef.current, machines, "%/work_configuration", parseWorkConfiguration);

    const [machineFilter, setMachineFilter] = useState(getStringFromCookie(Const.COOKIE_MACHINE_FILTER));
    const [parcelFilter, setParcelFilter] = useState(getStringFromCookie(Const.COOKIE_PARCEL_FILTER));

    const [clicks, setClicks] = useState(0); // TODO remove

    const [selectedMachineName, setSelectedMachineName] = useState("");
    const [selectedFieldName, setSelectedFieldName] = useState(null);

    const [editedParcel, setEditedParcel] = useState(null);

    const livePositionsRef = useRef({});

    const machineFieldMapping = useMachineFieldBidirectionalMapping(parcelsWithHulls);

    const highlightedMachines = useMemo(() => {
        return machineFieldMapping.getMachineNames(selectedFieldName);
    }, [machineFieldMapping, selectedFieldName])

    // const [managedUsersVersion, setManagedUsersVersion] = useState(0);
    // const managedUsers = useManagedUsers(session, userId, managedUsersVersion);

    useEffect(() => {
        // console.log("MAIN updating live position reference, version", livePositions[VERSION_TAG]);
        livePositionsRef.current = livePositions;
    }, [selectedFieldName, selectedMachineName, livePositions]);


    const handleMachineClicked = useCallback((name) => {
        if (!editedParcel) {
            console.log(TAG, "machine clicked:", name, "selected machine name:", selectedMachineName);
            if (name === selectedMachineName) {
                setSelectedMachineName(undefined);
            } else {
                setSelectedFieldName(null)
                setSelectedMachineName(name);
            }
        }
    }, [editedParcel, selectedMachineName]);


    const handleParcelClicked = useCallback((parcel) => {
        if (!editedParcel) {
            console.log(TAG, "parcel clicked", parcel, "selected parcel:", selectedFieldName);
            if (parcel['field_name'] === selectedFieldName) {
                setSelectedFieldName(undefined);
            } else {
                setSelectedFieldName(parcel['field_name']);
            }
        }
    }, [editedParcel, selectedFieldName]);


    function handleMachineFilterChanged(f) {
        // console.log("machine filter changed", f);
        if (selectedMachineName && !selectedMachineName.toLowerCase().includes(f)) {
            setSelectedMachineName(null);
        }
        setMachineFilter(f);
        setCookieString(Const.COOKIE_MACHINE_FILTER, f);
    }

    function handleParcelFilterChanged(f) {
        // console.log("parcel filter changed", f);
        setParcelFilter(f);
        setCookieString(Const.COOKIE_PARCEL_FILTER, f);
    }

    function handleParcelVisibilityClicked(name) {
        if (hiddenParcels.has(name)) {
            hiddenParcels.delete(name);
        } else {
            hiddenParcels.add(name);
        }
        console.log(TAG, "hidden parcels", hiddenParcels);
        const hiddenSet = new Set(hiddenParcels);
        setHiddenParcels(new Set(hiddenSet));
        setCookieSet(Const.COOKIE_HIDDEN_PARCELS, hiddenSet);
    }

    function handleParcelEditingClicked(parcel) {
        // console.log(TAG, "parcel editing", parcel)
        if (editedParcel === parcel) {
            setEditedParcel(null);
        } else {
            setEditedParcel(parcel);
        }
        // setEditedParcel(parcel);
    }

    function handleParcelEditingCancelClicked() {
        setEditedParcel(null);
    }


    // TODO remove
    function updateClicks() {
        let count = 0;
        const intervalId = setInterval(() => {
            if (count < 100) {
                setClicks(prevState => prevState + 1);
                count++;
            } else {
                clearInterval(intervalId);
            }
        }, 100);
    }





    return (
        <div className={""}>
            <OverviewMapV4
                parcels={parcelsWithHulls}
                hiddenParcels={hiddenParcels}
                machines={machines}
                livePositions={livePositions}
                velocities={machineVelocities}
                navigationSkills={navigationSkills}
                workConfigurations={workConfigurations}
                selectedMachineName={selectedMachineName}
                selectedFieldName={selectedFieldName}
                parcelFilter={parcelFilter}
                onParcelClicked={handleParcelClicked}
                onMachineClicked={name => handleMachineClicked(name)}
                editedParcel={editedParcel}
                onEditedParcelCancelClicked={handleParcelEditingCancelClicked}
                counter={clicks}
                updateCounter={updateClicks}
            >
            </OverviewMapV4>

            <div className="position-absolute top-0 start-50 translate-middle-x my-0 p-2 align-self-center" style={{zIndex: Const.PANEL_OVERLAY_Z + 10, maxWidth: "70%"}}>
                <UserHeader userId={userId} userName={userName} remoteAddr={remoteAddr}/>
            </div>

            {session
                ?
                <div className={"position-absolute start-0 top-0 m-0 p-2 d-flex"} style={{zIndex: Const.PANEL_OVERLAY_Z, maxHeight: "100%", maxWidth: "25%"}}>
                    <OverviewMachineList
                        mqttClient={mqttRef.current}
                        machines={machines}
                        machineStates={machineStates}
                        livePositions={livePositions}
                        navigationSkills={navigationSkills}
                        selectedMachineName={selectedMachineName}
                        machineFilter={machineFilter}
                        highlightedMachines={highlightedMachines}
                        selectedFieldName={selectedFieldName}
                        onMachineFilterChanged={handleMachineFilterChanged}
                        onMachineClicked={handleMachineClicked}/>
                </div>
                : null
            }

            {session
                ?
                <div className={"position-absolute end-0 top-0 m-0 p-2 d-flex"} style={{zIndex: Const.PANEL_OVERLAY_Z, maxHeight: "100%", maxWidth: "25%"}}>
                    <OverviewParcelList
                        mqttClient={mqttRef.current}
                        parcels={parcelsWithHulls}
                        isParcelsLoading={isParcelsLoading}
                        parcelFilter={parcelFilter}
                        onParcelFilterChanged={handleParcelFilterChanged}
                        hiddenParcels={hiddenParcels}
                        onParcelVisibilityClicked={handleParcelVisibilityClicked}
                        machineFilter={machineFilter}
                        selectedMachineName={selectedMachineName}
                        selectedFieldName={selectedFieldName}
                        workConfigurations={workConfigurations}
                        onParcelClicked={handleParcelClicked}
                        editedParcel={editedParcel}
                        onParcelEditClicked={handleParcelEditingClicked}
                    />
                </div>
                : null
            }

        </div>

    );


}


export default MainPageV2;